import {createAsyncThunk} from '@reduxjs/toolkit'
import {go, RouterState} from 'redux-first-history'

import {isApiError} from '@shared/helpers/api'
import {eq} from '@shared/helpers/lang'

import {scrollTo, systemAlert, systemConfirm} from '@web/actions/application'
import {
    AddressBookRequestFailMessage,
    AddressBookRequestFailMessageGetter,
    DefaultAddressBookRequestFailMessage,
    DELIVERY_MANAGE_SCENE,
    DELIVERY_MEMO_ERROR_MESSAGE,
    DELIVERY_VALIDATION_RESULT_TYPE,
    isAddressBookRequestFailMessageGetterKey,
    isAddressBookRequestFailMessageKey,
    maxAddressBookExceededMessage,
    ScrollType,
} from '@web/core/constants'
import {
    validateAddress,
    validateVirtualPhoneNumberAvailable,
    checkMemoIncludesInvisibleExceptSpace,
} from '@web/helpers/policies'
import {
    BaseDeliveryAddress,
    DELIVERY_MANAGER_TYPE,
    DeliveryAddress,
    deliveryAddressPayloadSelector,
    deliveryManagerCaseSelector,
    deliveryManagerSelector,
    type DeliveryManagerState,
    getEmptyDeliveryAddress,
    getInitialAddressSearchQuery,
    saveDeliveryManagerSessionStorage,
    saveStoragePayloadSelector,
} from '@web/slices/deliveryManager/slice'
import {locationSelector, push} from '@web/slices/router'

import addressBookApi from './apis/addressBook'
import {
    checkDoneCondition,
    getDeliveryAddressBadge,
    getSaveAddressType,
    increaseHistoryCountFromBase,
    SaveAddressType,
} from './utils'

/** 배송지목록에서 수정/저장버튼 클릭시 해당 배송지에 대한 수정화면 이동 */
export const goAddressForm = createAsyncThunk<
    DeliveryAddress,
    Maybe<BaseDeliveryAddress>,
    {
        state: {
            router: RouterState
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/goAddressForm', async (deliveryAddress, {dispatch, rejectWithValue, getState}) => {
    const {pathname, search, state: locationState} = locationSelector(getState())
    const {
        deliveryAddressBook: {isAddible},
    } = deliveryManagerSelector(getState())

    // hash가 없거나 (신규), 미등록이면 (최근배송지 저장) 주소 추가 가능여부 조회 필요
    const shouldCheckAddible = !deliveryAddress?.hash || !deliveryAddress.isRegistered

    if (shouldCheckAddible && !isAddible) {
        return rejectWithValue(maxAddressBookExceededMessage)
    }

    const url = `${pathname}${search}`
    dispatch(
        push(url, {
            state: {
                ...locationState,
                deliveryManagerScene: DELIVERY_MANAGE_SCENE.FORM,
                historyCountFromBase: increaseHistoryCountFromBase(locationState?.historyCountFromBase),
            },
        }),
    )

    const deliveryAddressPayload = {
        ...getEmptyDeliveryAddress(),
        ...deliveryAddress,
        addressNameBadge: getDeliveryAddressBadge(deliveryAddress?.addressName || ''),
        isVirtualPhoneNumberUsed: false,
    }

    const saveStoragePayload = saveStoragePayloadSelector(getState())
    saveDeliveryManagerSessionStorage({
        ...saveStoragePayload,
        deliveryAddress: deliveryAddressPayload,
    })

    return deliveryAddressPayload
})

/** 배송지 수정 폼 화면에서 확인버튼 클릭. */
export const confirmAddressForm = createAsyncThunk<
    BaseDeliveryAddress,
    void,
    {state: {deliveryManager: DeliveryManagerState}}
>('deliveryManager/confirmAddressForm', async (_, {getState, dispatch, rejectWithValue}) => {
    const {
        deliveryAddress,
        orderInfo: {
            isOverseasDelivery,
            hasArrivalGuaranteeProduct,
            virtualPhoneNumber,
            deliveryMemo: {memoList},
        },
        options: {type},
        status: {historyCountFromBase},
    } = deliveryManagerSelector(getState())

    if (!deliveryAddress) {
        return rejectWithValue(DefaultAddressBookRequestFailMessage)
    }

    const addressPayload = deliveryAddressPayloadSelector(getState())

    // 공통 주소 validation
    const {type: validationResultType, message} = validateAddress({
        addressInfo: addressPayload,
        isOverseasDelivery,
        hasArrivalGuaranteeProduct,
        ignoreField: type === DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY ? ['detailAddress'] : [],
    })

    const eqValidationResultType = eq(validationResultType)

    if (eqValidationResultType(DELIVERY_VALIDATION_RESULT_TYPE.INVALID)) {
        dispatch(systemAlert({type: 'ALERT', message}))
        return rejectWithValue('배송지 저장에 실패했습니다.')
    } else if (eqValidationResultType(DELIVERY_VALIDATION_RESULT_TYPE.WARNING)) {
        const isConfirmed = await new Promise<boolean>((resolve) => {
            dispatch(
                systemConfirm({
                    type: 'CONFIRM',
                    message,
                    callback: (confirmed: boolean) => {
                        resolve(confirmed)
                    },
                }),
            )
        })
        if (!isConfirmed) {
            return rejectWithValue('배송지 저장에 실패했습니다.')
        }
    }

    // 연락처변경타입 validation
    if (type === DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY) {
        /** 연락처 1, 2를 받았을 때 해당번호를 안심번호로 사용할 수 있는지 여부 검증. */
        const {type: validationVirtualPhoneNumberResultType, message: virtualPhoneNumberUseErrorMsg} =
            validateVirtualPhoneNumberAvailable({telNo1: addressPayload.telNo1, telNo2: addressPayload.telNo2})
        const eqValidationVirtualPhoneNumber = eq(validationVirtualPhoneNumberResultType)

        if (virtualPhoneNumber.used && eqValidationVirtualPhoneNumber(DELIVERY_VALIDATION_RESULT_TYPE.INVALID)) {
            dispatch(systemAlert({type: 'ALERT', message: virtualPhoneNumberUseErrorMsg}))
            return rejectWithValue(virtualPhoneNumberUseErrorMsg)
        }

        // 배송메모 validation. 연락처변경 시 주문형BE에 메모정보도 같이 보내줘야 해서 같이 검증.
        const validateMemo = checkMemoIncludesInvisibleExceptSpace
        if (memoList.some((memo) => validateMemo(memo.value))) {
            dispatch(
                systemAlert({type: 'ALERT', message: DELIVERY_MEMO_ERROR_MESSAGE.MEMO_INCLUDE_INVISIBLE_CHAR_FAIL}),
            )
            return rejectWithValue(DELIVERY_MEMO_ERROR_MESSAGE.MEMO_INCLUDE_INVISIBLE_CHAR_FAIL)
        }
    }

    // 연락처 변경타입은 배송지저장요청을 하지 않고, 외부로 넘김.
    if (type === DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY) {
        return deliveryAddress
    } else {
        let hash = addressPayload.hash
        const saveType = getSaveAddressType({hash: addressPayload.hash, isRegistered: addressPayload.isRegistered})
        const eqSaveType = eq(saveType)
        try {
            if (eqSaveType(SaveAddressType.EDIT)) {
                const {hash: newHash} = await addressBookApi.editAddress({
                    hash: addressPayload.hash ?? '',
                    address: addressPayload,
                })
                hash = newHash
            } else {
                // ADD, REGISTER
                const {hash: newHash} = await addressBookApi.addAddress({address: addressPayload})
                hash = newHash
            }
        } catch (e) {
            if (isApiError(e)) {
                const code = e.response?.data?.result?.code
                const usingServices = e.response?.data?.result?.usingServices

                if (code && typeof code === 'string') {
                    let errorMessage = DefaultAddressBookRequestFailMessage
                    if (isAddressBookRequestFailMessageKey(code)) {
                        errorMessage = AddressBookRequestFailMessage[code]
                    } else if (isAddressBookRequestFailMessageGetterKey(code)) {
                        errorMessage = AddressBookRequestFailMessageGetter[code]({method: 'put', usingServices})
                    }

                    return rejectWithValue(errorMessage)
                }
            }
            return rejectWithValue('배송지 저장에 실패했습니다.')
        }

        /**
         * 최근배송지 등록시에는 신규 등록배송지만 남기고 기등록되어있던 최근배송지를 삭제합니다.
         * 등록배송지 수정시에는 동일한 해시의 최근배송지를 삭제합니다.
         * 최근배송지 삭제 실패의 경우에도 별다른 오류 처리 없이 주문서로 복귀합니다.
         */
        try {
            if (eqSaveType(SaveAddressType.REGISTER) || eqSaveType(SaveAddressType.EDIT)) {
                await addressBookApi.deleteAddress({hash: addressPayload.hash, type: 'recent'})
            }
        } catch {}

        const saveStoragePayload = saveStoragePayloadSelector(getState())
        saveDeliveryManagerSessionStorage({
            ...saveStoragePayload,
            searchQuery: getInitialAddressSearchQuery(),
            address: null,
            deliveryAddress: null,
        })

        // 배송지리스트에서 수정화면으로 진입 케이스의 히스토리 처리.
        // - 배송지리스트로 돌아간 후 배송지리스트에서 뒤로가기로 수정화면 재진입 방지
        // - 그 외 케이스는 closeWithSuccess 액션에서 처리
        const shouldGoAddressBook = !checkDoneCondition({
            openType: type || '',
            actionType: 'deliveryManager/confirmAddressForm',
        })
        shouldGoAddressBook && dispatch(go(-historyCountFromBase))

        return {...deliveryAddress, hash}
    }
})

/** 배송지목록(List, Change)에서 배송지 선택 액션 */
export const selectDeliveryAddress = createAsyncThunk<
    BaseDeliveryAddress,
    {selectedAddress: BaseDeliveryAddress; portal?: Maybe<HTMLElement>},
    {
        state: {
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/selectDeliveryAddress', async ({selectedAddress, portal}, {dispatch, getState, rejectWithValue}) => {
    const {
        orderInfo: {isOverseasDelivery, hasArrivalGuaranteeProduct},
    } = deliveryManagerSelector(getState())
    const {isResultCase, isOrdersheetCase} = deliveryManagerCaseSelector(getState())

    // 여기는 주문완료/상세 > 배송지변경창 > 배송지 선택 시 선택된 내용을 저장하면서 아래로 스크롤하는 로직.
    if (isResultCase) {
        dispatch(
            scrollTo({
                scrollType: ScrollType.BOTTOM,
                customScrollBody: portal,
            }),
        )
    }

    // 주문서 > 배송지 선택 시 검증
    if (isOrdersheetCase) {
        const {type, message} = validateAddress({
            addressInfo: selectedAddress,
            isOverseasDelivery,
            hasArrivalGuaranteeProduct,
        })

        try {
            const eqType = eq(type)

            if (eqType(DELIVERY_VALIDATION_RESULT_TYPE.INVALID)) {
                dispatch(systemAlert({type: 'ALERT', message}))
                return rejectWithValue('배송지를 변경하지 않았습니다.')
            } else if (eqType(DELIVERY_VALIDATION_RESULT_TYPE.WARNING)) {
                const isConfirmed = await new Promise<boolean>((resolve) => {
                    dispatch(
                        systemConfirm({
                            type: 'CONFIRM',
                            message,
                            callback: (confirmed) => resolve(confirmed),
                        }),
                    )
                })
                if (!isConfirmed) {
                    return rejectWithValue('배송지를 변경하지 않았습니다.')
                }
            }
        } catch (e) {
            if (isApiError(e)) {
                return rejectWithValue(e.response?.data)
            }
            throw e
        }
    }

    return selectedAddress
})
